import { FC } from 'react';
import { useTheme } from '../context/ThemeContext.js';
import { Cloud, Brain, Shield, BarChart2, Code, MessageSquare } from 'lucide-react';

export const ServicesPage: FC = () => {
  const { theme } = useTheme();
  
  // Theme-based styles
  const themeStyles = {
    light: {
      background: 'bg-gray-50',
      text: 'text-gray-900',
      card: 'bg-white',
      cardText: 'text-gray-700',
      sectionTitle: 'text-gray-800',
      cardHover: 'hover:shadow-lg hover:-translate-y-1',
      featureText: 'text-gray-600',
      iconBg: 'bg-blue-100',
      iconText: 'text-blue-600'
    },
    dark: {
      background: 'bg-gray-900',
      text: 'text-gray-100',
      card: 'bg-gray-800',
      cardText: 'text-gray-300',
      sectionTitle: 'text-white',
      cardHover: 'hover:shadow-xl hover:shadow-blue-900/10 hover:-translate-y-1',
      featureText: 'text-gray-400',
      iconBg: 'bg-blue-900 bg-opacity-50',
      iconText: 'text-blue-200'
    }
  };

  // Get current theme styles, default to dark if in system mode
  const currentTheme = theme === 'system' 
    ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    : theme;
  const styles = themeStyles[currentTheme as keyof typeof themeStyles] || themeStyles.dark;
  const services = [
    {
      title: 'Cloud Solutions',
      description: 'Comprehensive cloud services including migration, management, and optimization across all major platforms.',
      icon: <Cloud className="w-8 h-8" />,
      features: [
        'Cloud Migration Services',
        'Multi-Cloud Strategy',
        'Cloud Cost Optimization',
        'Disaster Recovery',
        'Cloud Security & Compliance'
      ]
    },
    {
      title: 'Data & Analytics',
      description: 'Transform your data into actionable insights with our advanced analytics solutions.',
      icon: <BarChart2 className="w-8 h-8" />,
      features: [
        'Business Intelligence',
        'Data Warehousing',
        'Predictive Analytics',
        'Data Visualization',
        'Big Data Solutions'
      ]
    },
    {
      title: 'Cybersecurity',
      description: 'Protect your digital assets with our comprehensive security solutions.',
      icon: <Shield className="w-8 h-8" />,
      features: [
        'Security Assessments',
        'Threat Detection & Response',
        'Identity & Access Management',
        'Compliance Management',
        'Security Awareness Training'
      ]
    },
    {
      title: 'AI & Machine Learning',
      description: 'Leverage the power of AI to drive innovation and efficiency.',
      icon: <Brain className="w-8 h-8" />,
      features: [
        'Machine Learning Models',
        'Natural Language Processing',
        'Computer Vision',
        'Predictive Analytics',
        'AI Strategy & Consulting'
      ]
    },
    {
      title: 'Custom Software Development',
      description: 'Data-driven digital marketing strategies to grow your online presence.',
      icon: <Code className="w-8 h-8" />,
      features: [
        'SEO Optimization',
        'Content Marketing',
        'Social Media Management',
        'PPC Advertising'
      ]
    },
    {
      title: 'IT Consulting',
      description: 'Expert technology consulting to align IT strategy with your business goals.',
      icon: <MessageSquare className="w-8 h-8" />,
      features: [
        'Technology Roadmapping',
        'Digital Transformation',
        'IT Infrastructure',
        'Security Audits'
      ]
    }
  ];

  return (
    <div className={`min-h-screen py-16 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${styles.background} ${styles.text}`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h1 className={`text-4xl font-extrabold sm:text-5xl sm:tracking-tight lg:text-6xl ${styles.sectionTitle}`}>
            Our Services
          </h1>
          <p className={`mt-3 max-w-3xl mx-auto text-xl ${styles.featureText} sm:mt-5`}>
            Comprehensive IT solutions tailored to your business needs
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 mt-12 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
          {services.map((service, index) => (
            <div
              key={index}
              className={`flex flex-col items-center text-center overflow-hidden ${styles.card} rounded-xl shadow-md ${styles.cardHover} transition-all duration-300`}
            >
              <div className={`w-16 h-16 rounded-full ${styles.iconBg} flex items-center justify-center text-3xl my-6`}>
                <span className={styles.iconText}>
                  {service.icon}
                </span>
              </div>
              <div className="w-full px-6 pb-6">
                <h3 className={`text-xl font-semibold ${styles.sectionTitle} mb-3`}>{service.title}</h3>
                <p className={`mt-3 text-base ${styles.cardText}`}>{service.description}</p>
                <div className="mt-6 text-left">
                  <h4 className={`text-sm font-medium ${styles.sectionTitle} mb-3`}>Key Features:</h4>
                  <ul className="mt-3 space-y-2">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <svg
                          className="flex-shrink-0 h-5 w-5 text-green-500"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className={`ml-2 text-sm ${styles.featureText}`}>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="p-6 pt-0 mt-auto">
                <button
                  type="button"
                  className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-300"
                >
                  Learn more
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
